// src/api/axios.js
// Central Axios instance — attaches JWT, handles 401 refresh automatically

import axios from 'axios';

const api = axios.create({
  baseURL:         import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,          // send httpOnly refresh cookie
  timeout:         15000,
  headers:         { 'Content-Type': 'application/json' },
});

// ── Request interceptor — attach access token ────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ke_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — silent token refresh on 401 ──────────────────
let isRefreshing  = false;
let failedQueue   = [];   // requests waiting for new token

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry   = true;
      isRefreshing      = true;

      try {
        const { data } = await api.post('/auth/refresh');
        const newToken  = data.token;
        localStorage.setItem('ke_token', newToken);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('ke_token');
        window.dispatchEvent(new Event('ke:logout'));   // AuthContext listens
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
